import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { request } from '../api/client';

interface AuditoriaItem {
  clienteId: string;
  nome: string;
  telefone: string;
  email: string | null;
  diasDesdeCompra: number | null;
  baseJanelaNula: boolean;
  tiragensForaJanelaNoPeriodo: number;
  creditosPagosTotal: number;
  creditosTiragemSaldo: number;
  limboSuspeito: boolean;
  excedenteSemCobranca: number;
  ultimaTiragemEm: string;
}
interface AuditoriaResult {
  janelaDias: number;
  periodo: { desde: string | null; ate: string | null };
  items: AuditoriaItem[];
}

interface RepeticaoItem {
  clienteId: string | null;
  conversaId: string;
  nome: string;
  telefone: string;
  incidentes: number;
  ultimoEm: string;
  exemploTrecho: string;
}
interface RepeticaoResult {
  periodo: { desde: string | null; ate: string | null };
  items: RepeticaoItem[];
}

function dtBR(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AuditoriaPage(): JSX.Element {
  const [desde, setDesde] = useState('');
  const [ate, setAte] = useState('');

  const { data, isLoading, isError, refetch, isFetching } = useQuery<AuditoriaResult>({
    queryKey: ['auditoria-tiragens-gratis', desde, ate],
    queryFn: () => {
      const p = new URLSearchParams();
      if (desde) p.set('desde', desde);
      if (ate) p.set('ate', ate);
      const q = p.toString();
      return request<AuditoriaResult>(`/admin/auditoria/tiragens-gratis${q ? `?${q}` : ''}`);
    },
  });

  const items = data?.items ?? [];

  const repeticao = useQuery<RepeticaoResult>({
    queryKey: ['auditoria-repeticao', desde, ate],
    queryFn: () => {
      const p = new URLSearchParams();
      if (desde) p.set('desde', desde);
      if (ate) p.set('ate', ate);
      const q = p.toString();
      return request<RepeticaoResult>(`/admin/auditoria/repeticao${q ? `?${q}` : ''}`);
    },
  });
  const repItems = repeticao.data?.items ?? [];

  return (
    <section aria-labelledby="auditoria-heading" className="space-y-4">
      <header>
        <h1 id="auditoria-heading" className="text-2xl font-semibold">
          Auditoria — tiragens grátis fora da janela
        </h1>
        <p className="muted text-sm mt-1">
          Lista as pessoas a quem a Sara entregou tiragem de cartas DE GRAÇA depois da janela de{' '}
          {data?.janelaDias ?? 7} dias — ou seja, quando deveria ter cobrado. Conta só tiragens
          cobráveis (intent tiragem, não pagas), ignorando carta do dia, sonho/sinais e saudações
          que só ecoam cartas. Créditos de pacote contam pela quantidade real. Se a pessoa tem{' '}
          <strong>saldo vivo</strong> e mesmo assim aparece aqui, é o “limbo de crédito” (crédito
          que nunca foi debitado). Quem aparece vale revisar a conversa.
        </p>
      </header>

      <div className="card flex flex-wrap items-end gap-3">
        <div>
          <span className="label block">Período (data das tiragens)</span>
          <p className="text-xs text-sara-text-muted mb-1">vazio = últimos 7 dias</p>
          <div className="flex items-end gap-2">
            <label className="block">
              <span className="sr-only">De</span>
              <input
                type="date"
                className="input"
                value={desde}
                max={ate || undefined}
                onChange={(e) => setDesde(e.target.value)}
              />
            </label>
            <span className="text-sara-text-muted">→</span>
            <label className="block">
              <span className="sr-only">Até</span>
              <input
                type="date"
                className="input"
                value={ate}
                min={desde || undefined}
                onChange={(e) => setAte(e.target.value)}
              />
            </label>
            {(desde || ate) && (
              <button
                type="button"
                className="text-sm text-sara-text-muted underline"
                onClick={() => {
                  setDesde('');
                  setAte('');
                }}
              >
                limpar
              </button>
            )}
          </div>
        </div>
        <button
          type="button"
          className="btn-primary"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          {isFetching ? 'Buscando…' : '↻ Atualizar'}
        </button>
      </div>

      <p className="text-sm text-sara-text-muted">
        {isLoading
          ? 'carregando…'
          : `${items.length} pessoa(s) com tiragem grátis indevida no período`}
      </p>

      {isError && (
        <div className="card text-sm text-sara-danger">
          erro ao carregar.{' '}
          <button type="button" className="underline" onClick={() => void refetch()}>
            tentar de novo
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-sara-text-muted border-b border-sara-border">
              <th className="py-2 pr-3 font-medium">Pessoa</th>
              <th className="py-2 pr-3 font-medium">Dias da compra</th>
              <th className="py-2 pr-3 font-medium">Tiragens fora da janela</th>
              <th className="py-2 pr-3 font-medium">Créditos pagos</th>
              <th className="py-2 pr-3 font-medium">Saldo vivo</th>
              <th className="py-2 pr-3 font-medium">Grátis indevidas</th>
              <th className="py-2 pr-3 font-medium">Última tiragem</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.clienteId} className="border-b border-sara-border/40">
                <td className="py-2 pr-3">
                  {c.nome}
                  <span className="block text-xs text-sara-text-muted">{c.telefone}</span>
                  <span className="block text-xs text-sara-text-muted">
                    {c.email ?? 'sem email'}
                  </span>
                </td>
                <td className="py-2 pr-3">
                  {c.diasDesdeCompra ?? '—'}
                  {c.baseJanelaNula && (
                    <span
                      className="block text-xs text-sara-danger"
                      title="Cliente sem data de compra (primeiraCompra NULL) — era o furo principal do vazamento"
                    >
                      sem data de compra
                    </span>
                  )}
                </td>
                <td className="py-2 pr-3">{c.tiragensForaJanelaNoPeriodo}</td>
                <td className="py-2 pr-3">{c.creditosPagosTotal}</td>
                <td className="py-2 pr-3">
                  {c.creditosTiragemSaldo}
                  {c.limboSuspeito && (
                    <span
                      className="block text-xs text-sara-danger"
                      title="Tem crédito vivo mas recebeu tiragem grátis = limbo de crédito (o crédito nunca é debitado, então lê de graça indefinidamente)"
                    >
                      limbo de crédito
                    </span>
                  )}
                </td>
                <td className="py-2 pr-3 font-semibold text-sara-danger">
                  {c.excedenteSemCobranca}
                </td>
                <td className="py-2 pr-3">{dtBR(c.ultimaTiragemEm)}</td>
              </tr>
            ))}
            {data && items.length === 0 && !isLoading && (
              <tr>
                <td colSpan={7} className="py-4 text-center text-sara-text-muted">
                  nenhuma tiragem grátis indevida detectada no período 🎉
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <header className="pt-4 border-t border-sara-border">
        <h2 className="text-xl font-semibold">Repetição — Sara reenviou resposta quase-idêntica</h2>
        <p className="muted text-sm mt-1">
          Conversas em que a Sara mandou uma resposta quase idêntica a uma que ela já tinha enviado
          antes na mesma conversa (o bug do caso Elenita: a cliente reclamou de repetição e recebeu
          o mesmo texto). Usa a MESMA detecção do guard que hoje previne isso em tempo real, então
          serve pra ver casos históricos e monitorar regressão. Usa o mesmo período do filtro acima.
        </p>
      </header>

      <p className="text-sm text-sara-text-muted">
        {repeticao.isLoading
          ? 'carregando…'
          : `${repItems.length} conversa(s) com repetição near-verbatim no período`}
      </p>

      {repeticao.isError && (
        <div className="card text-sm text-sara-danger">
          erro ao carregar repetição.{' '}
          <button type="button" className="underline" onClick={() => void repeticao.refetch()}>
            tentar de novo
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-sara-text-muted border-b border-sara-border">
              <th className="py-2 pr-3 font-medium">Pessoa</th>
              <th className="py-2 pr-3 font-medium">Repetições</th>
              <th className="py-2 pr-3 font-medium">Último caso</th>
              <th className="py-2 pr-3 font-medium">Exemplo</th>
            </tr>
          </thead>
          <tbody>
            {repItems.map((c) => (
              <tr key={c.conversaId} className="border-b border-sara-border/40">
                <td className="py-2 pr-3">
                  {c.nome}
                  <span className="block text-xs text-sara-text-muted">{c.telefone}</span>
                </td>
                <td className="py-2 pr-3 font-semibold text-sara-danger">{c.incidentes}</td>
                <td className="py-2 pr-3">{dtBR(c.ultimoEm)}</td>
                <td className="py-2 pr-3 text-xs text-sara-text-muted max-w-md truncate">
                  {c.exemploTrecho}
                </td>
              </tr>
            ))}
            {repeticao.data && repItems.length === 0 && !repeticao.isLoading && (
              <tr>
                <td colSpan={4} className="py-4 text-center text-sara-text-muted">
                  nenhuma repetição near-verbatim detectada no período 🎉
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
